// The machine-verifiable evidence pack (JSON). The PDF/A pack is a human rendering of this; the
// verifier CLI and the /verify page check this structure.
import { fromB64url, sha256Hex, b64url } from "./bytes.ts";
import { leafHash, verifyInclusion } from "./merkle.ts";
import { verifySegment, type SegmentEntry, type VerifyIssue } from "./chain.ts";
import { parseTimestampResponse } from "./tsa.ts";

export interface AnchorProof {
  root_id: string;
  root_hash: string;
  head: { site_id: string; seq: number; hash: string };
  index: number;
  size: number;
  path: string[];
  tsa: { url: string; token_b64: string; serial: string; gen_time: string } | null;
  ots_b64: string | null;
}

export interface EvidencePack {
  v: 1;
  // "pickmeup.evidence": packs issued before the rename to Vavr, which schools keep and must still verify.
  kind: "vavr.evidence" | "pickmeup.evidence";
  site_id: string;
  site_name: string;
  generated_at: number;
  subject: { event_ids: string[] };
  devices: Record<string, string>;
  start_prev_hash: string | null;
  segment: SegmentEntry[];
  anchor: AnchorProof | null;
  blobs: Record<string, { sha256: string; content_type: string; data_b64?: string }>;
}

export interface PackReport { ok: boolean; anchored: boolean; issues: VerifyIssue[]; anchoredAt: string | null; head: string | null }

export async function verifyPack(pack: EvidencePack): Promise<PackReport> {
  const issues: VerifyIssue[] = [];
  if (pack.v !== 1 || (pack.kind !== "vavr.evidence" && pack.kind !== "pickmeup.evidence")) return { ok: false, anchored: false, issues: [{ seq: null, problem: "not a Vavr evidence pack" }], anchoredAt: null, head: null };
  const chain = await verifySegment(pack.segment, { siteId: pack.site_id, devices: pack.devices, startPrevHash: pack.start_prev_hash ?? undefined });
  issues.push(...chain.issues);

  const subjects = new Set(pack.subject.event_ids);
  for (const e of pack.segment) if (e.kind === "event") subjects.delete(e.event.pre.id);
  for (const id of subjects) issues.push({ seq: null, problem: `subject event ${id} is not in the segment` });

  for (const [name, blob] of Object.entries(pack.blobs)) {
    if (blob.data_b64 !== undefined && (await sha256Hex(fromB64url(blob.data_b64))) !== blob.sha256) issues.push({ seq: null, problem: `blob ${name} does not match its hash` });
  }
  for (const e of pack.segment) {
    if (e.kind !== "event") continue;
    for (const h of [e.event.pre.photo_sha256, e.event.pre.stroke_sha256]) {
      if (h && !Object.values(pack.blobs).some((b) => b.sha256 === h)) issues.push({ seq: e.event.seq, problem: `referenced blob ${h.slice(0, 12)}… is not included` });
    }
  }

  let anchoredAt: string | null = null;
  if (pack.anchor) {
    const a = pack.anchor;
    if (a.head.site_id !== pack.site_id) issues.push({ seq: null, problem: "anchor is for another site" });
    const last = pack.segment.at(-1);
    const lastSeq = last ? (last.kind === "event" ? last.event.seq : last.seq) : null;
    if (lastSeq !== a.head.seq || chain.head !== a.head.hash) issues.push({ seq: a.head.seq, problem: "segment does not end at the anchored head" });
    if (!(await verifyInclusion(await leafHash(a.head), a.index, a.size, a.path, a.root_hash))) issues.push({ seq: null, problem: "Merkle inclusion proof fails" });
    if (a.tsa) {
      try {
        const info = parseTimestampResponse(fromB64url(a.tsa.token_b64), true);
        if (info.hashedMessage !== a.root_hash) issues.push({ seq: null, problem: "timestamp token covers a different root" });
        anchoredAt = info.genTime;
      } catch (err) {
        issues.push({ seq: null, problem: `timestamp token unreadable: ${(err as Error).message}` });
      }
    }
  }
  return { ok: issues.length === 0, anchored: anchoredAt !== null, issues, anchoredAt, head: chain.head };
}

export { b64url };
